import { cn } from '@utazzo/ui';
import { CalendarClock, CheckCircle2 } from 'lucide-react';

import { KIND_META } from '../item-kind';
import { payByTone } from '../pay-by';

import type { OpsActionable, OpsActionableKind } from '@utazzo/shared';

/**
 * Per converted lead: what ops still has to book. One chip per kind with the
 * PENDING count big and the total small ("2 of 3"); a fully booked kind turns
 * green, a kind the trip does not have is hidden.
 */

const META = KIND_META;

export const ACTIONABLE_ORDER: OpsActionableKind[] = [
  'fixed_departure',
  'flight',
  'hotel',
  'activity',
  'transfer',
  'cruise',
  'visa',
  'insurance',
  'meal',
  'other',
];

export const ActionableChip = ({
  kind,
  pending,
  total,
  held = 0,
}: {
  kind: OpsActionableKind;
  pending: number;
  total: number;
  /** Of the pending ones, how many are on hold with the supplier. */
  held?: number;
}): React.JSX.Element => {
  const meta = META[kind];
  const Icon = meta.icon;
  const done = total > 0 && pending === 0;
  return (
    <span
      title={`${meta.plural}: ${pending} to book of ${total}${held ? `, ${held} on hold` : ''}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs ring-1 ring-inset',
        done ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : meta.tone,
      )}
    >
      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
      <span className="font-medium">{meta.label}</span>
      <span className="font-semibold tabular-nums">{done ? total : pending}</span>
      {!done && total !== pending ? (
        <span className="text-[10px] tabular-nums opacity-70">of {total}</span>
      ) : null}
      {!done && held > 0 ? (
        <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800 tabular-nums">
          {held} hold
        </span>
      ) : null}
    </span>
  );
};

export const ActionableCell = ({
  actionable,
  nextPaymentDue = null,
}: {
  actionable: OpsActionable;
  /** Earliest last-payment date among the lead's held items. */
  nextPaymentDue?: Date | null;
}): React.JSX.Element => {
  const kinds = ACTIONABLE_ORDER.filter((k) => actionable[k].total > 0);
  if (kinds.length === 0) {
    return <span className="text-xs text-zinc-400">No bookable items on the itinerary</span>;
  }
  const due = nextPaymentDue ? payByTone(nextPaymentDue) : null;
  return (
    <div className="max-w-[420px]">
      <div className="flex flex-wrap gap-1.5">
        {kinds.map((k) => (
          <ActionableChip
            key={k}
            kind={k}
            pending={actionable[k].pending}
            total={actionable[k].total}
            held={actionable[k].held}
          />
        ))}
      </div>
      {nextPaymentDue && due ? (
        <div
          className={cn(
            'mt-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1',
            due.className,
          )}
          title="Earliest last-payment date among held items"
        >
          <CalendarClock className="h-3 w-3" />
          Pay by{' '}
          {nextPaymentDue.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
          })}{' '}
          · {due.label}
        </div>
      ) : null}
    </div>
  );
};

export const actionableLabel = (kind: OpsActionableKind): string => META[kind].plural;
export const actionableIcon = (kind: OpsActionableKind) => META[kind].icon;
